import { Eye, Lock, Printer, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DocumentTemplateList } from '@/types/template';
import { getDocumentTemplatePreviewHtml } from '@/services/templateService';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import {
  TemplateCategory,
  CATEGORY_LABELS,
  getCategoryBadgeColor,
} from '../constants';

interface DocumentTemplatePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: DocumentTemplateList | null;
}

export function DocumentTemplatePreviewDialog({
  open,
  onOpenChange,
  template,
}: DocumentTemplatePreviewDialogProps) {
  const { toast } = useToast();

  const { data: previewData, isLoading: isPreviewingHtmlDocument } = useQuery({
    queryKey: ['templates', 'document-preview-html', template?.id],
    queryFn: () => getDocumentTemplatePreviewHtml(template!.id),
    enabled: open && !!template,
  });
  const previewHtml = previewData?.html ?? null;

  const handlePdfPreview = async () => {
    if (!template) return;
    try {
      const result = await getDocumentTemplatePreviewHtml(template.id);
      const win = window.open('', '_blank');
      if (!win) {
        toast({
          title: 'Error',
          description: 'Pop-up blocked. Please allow pop-ups for this site.',
          variant: 'destructive',
        });
        return;
      }
      win.document.write(result.html);
      win.document.close();
      win.onload = () => win.print();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate PDF preview',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Preview: {template?.name}
            {template?.source === 'SYSTEM' && (
              <Badge
                variant="outline"
                className="border-border text-muted-foreground gap-1 text-xs"
              >
                <Lock className="h-3 w-3" />
                System
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {template?.source === 'SYSTEM'
              ? 'This is the built-in system template. It cannot be edited but can be overridden by creating a custom template and setting it as active.'
              : 'Preview of the template rendered with sample data.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Template metadata + PDF button */}
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Category: </span>
                <Badge
                  className={getCategoryBadgeColor(
                    template?.template_type || ''
                  )}
                >
                  {
                    CATEGORY_LABELS[
                      (template?.template_type || 'other') as TemplateCategory
                    ]
                  }
                </Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Version: </span>
                <span className="font-medium">v{template?.version}</span>
              </div>
            </div>
            {template && (
              <Button variant="outline" size="sm" onClick={handlePdfPreview}>
                <Printer className="h-4 w-4 mr-2" />
                PDF Preview
              </Button>
            )}
          </div>

          {/* Rendered preview */}
          {template && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted px-3 py-2 border-b text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                Rendered Preview with sample data
              </div>
              <div className="p-4 bg-background min-h-[300px]">
                {isPreviewingHtmlDocument || !previewHtml ? (
                  <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full min-h-[400px] border-0"
                    title="Template preview"
                    sandbox=""
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
